import React, { useMemo, useState } from 'react'
import { Box, Button, Group, Select, Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'

import { CalendarCourtReporterAdminEvent } from '@/apiClient'
import { useModals } from '@/hooks/useModals'
import {
  useBookCourtReporterMutation,
  useCourtReportersQuery,
} from '@/queries/courtReporters'
import { formatDuration, formatTimeRange } from '@/utils/time'

type Props = {
  event: CalendarCourtReporterAdminEvent
  onBook: () => void
}

export const BookCourtReporterButton: React.FC<Props> = ({ event, onBook }) => {
  const modals = useModals()

  return (
    <Button
      onClick={() =>
        modals.openModal({
          title: 'Book a court reporter',
          children: (
            <ConfirmCourtReporterBookingModal event={event} onBook={onBook} />
          ),
          centered: true,
        })
      }
    >
      Book court reporter
    </Button>
  )
}

const ConfirmCourtReporterBookingModal: React.FC<Props> = ({
  event,
  onBook,
}) => {
  const modals = useModals()
  const [courtReporterId, setCourtReporterId] = useState<string | null>(null)
  const {
    data: allCourtReporters = [],
    isLoading: areCourtReportersLoading,
    isFetched: areCourtReportersFetched,
  } = useCourtReportersQuery()

  const courtReporters = useMemo(
    () => allCourtReporters.filter(cr => cr.is_active),
    [allCourtReporters],
  )

  const { mutateAsync: book, isLoading } = useBookCourtReporterMutation(event, {
    onSuccess: () => {
      // we don't use confirm modal here so we have to close the modal manually
      modals.closeAll()
      showNotification({
        message: <Text weight={600}>Court reporter booked</Text>,
      })
      onBook()
    },
    onErrorMessage: message => {
      modals.closeAll()
      showNotification({ color: 'red', message })
    },
  })

  return (
    <>
      <Box mb={20}>
        <Stack>
          <Text size="sm">
            {event.name} at {formatTimeRange(event.datetime, event.duration)} (
            {formatDuration(event.duration)})
          </Text>
          <Select
            label="Court reporter"
            data={courtReporters.map(cr => ({ value: cr.id, label: cr.name }))}
            value={courtReporterId}
            onChange={setCourtReporterId}
            placeholder={
              areCourtReportersLoading ? 'Loading...' : 'Select court reporter'
            }
            error={
              areCourtReportersFetched &&
              courtReporters.length === 0 &&
              'There are no active court reporters'
            }
            searchable
            withinPortal
          />
        </Stack>
      </Box>
      <Group position="right">
        <Button variant="light" color="gray" onClick={() => modals.closeAll()}>
          Cancel
        </Button>
        <Button
          variant="filled"
          disabled={courtReporterId === null}
          loading={isLoading}
          onClick={() => book({ court_reporter_id: courtReporterId! })}
        >
          Book court reporter
        </Button>
      </Group>
    </>
  )
}
